/**
 * Zod schemas for the Workflow Continuation Engine (Sprint 1).
 *
 * See:
 *   - docs/STATEFUL-WORKFLOW-PLATFORM-SPEC-v1.1-AMENDMENTS.md §5.3
 *   - c:/tmp/sprint-1-architecture.md
 *
 * CLAUDE.md Rule 26: Zod is the source of truth.
 */

import { z } from 'zod';

// ── Input to resumeWorkflow() ────────────────────────────────────────────
export const ResumeWorkflowInputSchema = z.object({
  /**
   * The automation_executions.id that is currently 'suspended'.
   * CAS guard will be a no-op if status != 'suspended'.
   */
  automationExecutionId: z.string().uuid(),

  /**
   * The agent_tool_approvals.id that was just decided.
   * This row's status, decided_evidence, and chosen_action_id are read
   * to determine which downstream edge to follow.
   */
  approvalId: z.string().uuid(),
});

export type ResumeWorkflowInput = z.infer<typeof ResumeWorkflowInputSchema>;

// ── Output of resumeWorkflow() ───────────────────────────────────────────
export const ResumeWorkflowOutputSchema = z.discriminatedUnion('status', [
  z.object({
    status: z.literal('no_op'),
    reason: z.enum([
      'execution_not_suspended', // CAS guard fired — already resumed or terminal
      'execution_not_found', // Row deleted by admin
    ]),
  }),
  z.object({
    status: z.literal('suspended'),
    // Execution re-suspended at a downstream approval node
    newApprovalId: z.string().uuid(),
    stepsExecuted: z.number().int().nonnegative(),
  }),
  z.object({
    status: z.literal('succeeded'),
    stepsExecuted: z.number().int().nonnegative(),
  }),
  z.object({
    status: z.literal('failed'),
    stepsExecuted: z.number().int().nonnegative(),
    error: z.string(),
  }),
]);

export type ResumeWorkflowOutput = z.infer<typeof ResumeWorkflowOutputSchema>;

// ── Frozen workflow snapshot stored at execution start (I16) ────────────
/**
 * WorkflowDefinitionSnapshot is stored in
 * automation_executions.workflow_definition_snapshot_jsonb.
 * Captured at the start of runWorkflow() so resume always reads a
 * frozen graph — never the live workflow_definitions row (Invariant I16).
 */
export const WorkflowDefinitionSnapshotSchema = z.object({
  // Provenance stamps
  automationId: z.string().uuid(),
  workflowDefinitionId: z.string().uuid(),
  snapshotAt: z.string().datetime(),
  workflowDefinitionUpdatedAt: z.string().datetime(),
  // Workflow identity
  name: z.string().max(200),
  purpose: z.string().nullable(),
  supervisorMode: z.enum(['minimal', 'standard', 'thorough']),
  // DAG
  nodes: z.array(z.object({
    id: z.string().uuid(),
    nodeKind: z.string().max(40),
    label: z.string().max(200).nullable(),
    config: z.record(z.string(), z.unknown()),
    positionX: z.number().int(),
    positionY: z.number().int(),
    displayOrder: z.number().int(),
  })),
  edges: z.array(z.object({
    id: z.string().uuid(),
    sourceNodeId: z.string().uuid(),
    targetNodeId: z.string().uuid(),
    sourceHandle: z.string().max(50).nullable(),
    label: z.string().max(100).nullable(),
  })),
  // Quality gates
  criteria: z.array(z.object({
    id: z.string().uuid(),
    description: z.string(),
    criteriaType: z.string().max(20),
    evaluation: z.string().max(20),
    weight: z.string().max(20),
  })),
  constraints: z.array(z.string()),
});

export type WorkflowDefinitionSnapshot = z.infer<typeof WorkflowDefinitionSnapshotSchema>;

// ── Supervisor context snapshot stored at execution start (ADR-0031) ────
/**
 * SupervisorContextSnapshot is stored in
 * automation_executions.supervisor_context_snapshot_jsonb.
 * Derived by deriveSupervisorContext() at runWorkflow() start.
 * Resume re-uses this snapshot so resumed steps are validated by the
 * same ruleset as pre-suspension steps (ADR-0031).
 */
export const SupervisorContextSnapshotSchema = z.object({
  derivedRole: z.string(),
  watchpoints: z.array(z.string()),
  redLines: z.array(z.string()),
  mode: z.enum(['minimal', 'standard', 'thorough']),
  /**
   * SHA-256(workflowDefinitionId + criteria.ids joined).
   * Used by auditors to detect if supervisor context drifted between
   * the snapshot and the live row. Defensive-only — resume always uses
   * the snapshot.
   */
  rulesetVersion: z.string().max(64),
});

export type SupervisorContextSnapshot = z.infer<typeof SupervisorContextSnapshotSchema>;

// ── entity_ids map stored in execution row ───────────────────────────────
/**
 * entity_ids tracks which business entities are in scope for this run.
 * Keys are entity kind names ('customer', 'deal', 'user', 'workflow').
 * Values are entity UUIDs or stable identifiers.
 * Populated by agent nodes that resolve an entity and want downstream
 * nodes to access entity-scoped memory.
 * Stored in automation_executions.entity_ids_jsonb.
 */
export const EntityIdsMapSchema = z.record(
  z.string().max(40), // entity kind
  z.string().max(255), // entity UUID or key
);

export type EntityIdsMap = z.infer<typeof EntityIdsMapSchema>;
